import { ListItemButton } from '@mui/material';
import { gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { fetchActiveLegByConsignment } from '@api/Trinium/Endpoints/Consignment/actions';
import { Leg } from '@api/Trinium/Endpoints/Leg';
import { useAppDispatch } from '@reduxStore';
import { queueModal } from '@components/Modals/modalSlice';
import { useMemo } from 'react';

/**
 * Manage Pickup/Delivery Action List Item that opens the Pickup/Delivery modal
 */
export default function ManageContainerAvailabilityAction() {
	const gridApiRef = useGridApiContext();
	const appDispatch = useAppDispatch();
	const selectedRowIds = useGridSelector(gridApiRef, gridRowSelectionIdsSelector);

	const selectedRows = useMemo(
		() =>
			Array.from(selectedRowIds.keys())
				.map((legId) => gridApiRef.current.getRow<DispatchLeg>(legId))
				.filter((row): row is DispatchLeg => row != null),
		[gridApiRef, selectedRowIds]
	);

	const hasEligibleLeg = useMemo(() => selectedRows.some((row) => row.legType === 'FCL' || row.legType === 'ECL'), [selectedRows]);

	// Import and Export loads follow reversed FCL/ECL leg chains, so a bulk update across a mixed selection isn't meaningful
	const hasMixedImportExport =
		selectedRows.some((row) => row.jobCategoryTransportationType === 'Import') &&
		selectedRows.some((row) => row.jobCategoryTransportationType === 'Export');

	// Nothing in the selection can take availability - offer no action rather than a modal with every field disabled
	if (selectedRows.length === 0 || (selectedRows.length > 1 && (!hasEligibleLeg || hasMixedImportExport))) {
		return <></>;
	}
	const leg = selectedRows[0];

	/** Callback method to open the modal when the Active Leg is determined */
	function consignmentCallback(activeLeg: Leg) {
		appDispatch(
			queueModal({
				Component: 'makeAvailableModal',
				Props: {
					legId: activeLeg.id
				}
			})
		);
	}

	const handleBtnClick = () => {
		// Multiple orders selected - apply availability changes across every selected leg's order that can take each value.
		if (selectedRows.length > 1) {
			appDispatch(
				queueModal({
					Component: 'bulkMakeAvailableModal',
					Props: {
						legs: selectedRows
					}
				})
			);
			return;
		}

		/** If the leg is workflow, we need to figure out the correct leg to use */
		if (leg.csnIsWorkflow) {
			fetchActiveLegByConsignment(leg.csnId, consignmentCallback);
		} else {
			/** Otherwise it is not workflow, open the modal */
			appDispatch(
				queueModal({
					Component: 'makeAvailableModal',
					Props: {
						legId: leg.legId
					}
				})
			);
		}
	};

	return <ListItemButton onClick={handleBtnClick}>Manage Container Availability</ListItemButton>;
}
